// External modules
import type { ReactNode } from 'react';
import { useStoreState } from '@/store';

// Internal modules
import { getContent, getLocale } from '@/lib';
import { features } from '@/lib/features';
import { isUserActionCommit } from '@/lib/globals';
import { useButtonCurrencyCode } from './useButtonCurrencyCode';
import { getFlowName, getSubFlow } from '../utils';
import { isLegacySubscription, isNextGenSubscription } from '@/lib/globals';

/*
Order DOES matter throughout this file - there is a mix of compliance-,
country-, and feature- specific logic throughout the file.
When changing or altering the order of things, please test thoroughly
*/
export const useSubmitButtonContent = (): {
  content: ReactNode;
  keyName: string;
} => {
  const { value: accountType } = useStoreState(
    ({ form }) => form.onboardOption,
  );
  const isExistingAccount = useStoreState(({ api }) => api.isExistingAccount);
  const isNextGenSub = isNextGenSubscription();

  const currency = useButtonCurrencyCode();

  if (
    features.isActive('isPaymentServiceActApplied') &&
    features.isActive('isBillingAgreement') &&
    !isNextGenSub
  ) {
    const buttonKey = isUserActionCommit()
      ? 'buttons.signup.continueAsMemberAndPay'
      : 'buttons.poma.continueAsMember';

    return { content: getContent(buttonKey), keyName: buttonKey };
  }

  const isStreamlineForcedExperience = features.isActive(
    'isStreamlineForcedExperience',
  );
  const isStreamlineOptionalExperience = features.isActive(
    'isStreamlineOptionalExperience',
  );

  // Silent Member Matching & Toggle Suppression scenarios
  const isSilentMemberMatching = features.isActive('isMemberMatchNoOptIn');
  const toggleSuppression = features.isActive(
    'isMemberMatchNoToggleWithInterstitial',
  );
  if (isExistingAccount && isSilentMemberMatching) {
    return {
      content: getContent('buttons.guest.base'),
      keyName: 'buttons.guest.base',
    };
  }
  if (isExistingAccount && toggleSuppression) {
    return {
      content: getContent('buttons.continueAsGuest'),
      keyName: 'buttons.continueAsGuest',
    };
  }

  // If currency conversion is needed, `conversionSelection` will
  // be "PAYPAL" or "VENDOR", otherwise it is an empty string
  const withConversion = Boolean(currency);
  const { country } = getLocale();
  const flowName = getFlowName(accountType);
  const subFlow = getSubFlow(withConversion);
  const isWPS = features.isActive('isWps');
  const isRecurringPayment = isLegacySubscription();
  const isECStreamline = isStreamlineOptionalExperience && !isWPS;
  const isWPSStreamline = isStreamlineOptionalExperience && isWPS;
  const deOnboardSwitchDefaultOverride = features.isActive(
    'hasGuestXOSepaUpdate',
  );

  // EC subscriptions button content
  if (flowName === 'signup' && isRecurringPayment) {
    return {
      content: getContent('buttons.signup.subscribe'),
      keyName: 'buttons.signup.subscribe',
    };
  }

  // Next gen EC subscriptions are both guest and signup eligible for now
  // but there are discussions around removing it from guest flows
  // due to compliance concerns
  if (isNextGenSub) {
    let contentKey;

    if (country === 'SG') {
      const userActionCommit = isUserActionCommit();
      if (flowName === 'guest') {
        contentKey = userActionCommit
          ? 'buttons.confirmAndSubscribe'
          : 'buttons.confirmAndContinue';
      } else {
        contentKey = userActionCommit
          ? 'buttons.signup.subscribe'
          : 'buttons.poma.continueAsMember';
      }
      return {
        content: getContent(contentKey),
        keyName: contentKey,
      };
    }

    contentKey =
      flowName === 'signup'
        ? 'buttons.signup.subscribe'
        : 'buttons.agreeAndSubscribe';
    return {
      content: getContent(contentKey),
      keyName: contentKey,
    };
  }

  if (
    isUserActionCommit() &&
    (isStreamlineForcedExperience ||
      (isStreamlineOptionalExperience && !isWPSStreamline))
  ) {
    if (flowName === 'signup') {
      return {
        content: getContent('buttons.signup.commit'),
        keyName: 'buttons.signup.commit',
      };
    }
    return {
      content: getContent('buttons.guest.commit'),
      keyName: 'buttons.guest.commit',
    };
  }

  // EC streamline button content
  if (flowName === 'guest' && isECStreamline) {
    if (deOnboardSwitchDefaultOverride) {
      return {
        content: getContent('buttons.guest.commit'),
        keyName: 'buttons.guest.commit',
      };
    }

    return {
      content: getContent('buttons.continueAsGuest'),
      keyName: 'buttons.continueAsGuest',
    };
  }
  if (flowName === 'signup' && isECStreamline) {
    return {
      content: getContent('buttons.poma.continueAsMember'),
      keyName: 'buttons.poma.continueAsMember',
    };
  }
  // WPS streamline button content
  if (flowName === 'guest' && isWPSStreamline) {
    if (deOnboardSwitchDefaultOverride) {
      return {
        content: getContent('buttons.guest.commit'),
        keyName: 'buttons.guest.commit',
      };
    }

    return {
      content: getContent('buttons.poma.payAsGuest'),
      keyName: 'buttons.poma.payAsGuest',
    };
  }
  if (flowName === 'signup' && isWPSStreamline) {
    return {
      content: getContent('buttons.poma.continueAsMemberAndPay'),
      keyName: 'buttons.poma.continueAsMemberAndPay',
    };
  }

  const components = [
    'buttons',
    flowName,
    withConversion && 'withConversion',
    subFlow,
  ];

  const contentKey = components.filter(Boolean).join('.');
  return { content: getContent(contentKey, { currency }), keyName: contentKey };
};
